import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { downloadExceptionsHtml } from "../../utils/exceptions-html-export";
import { downloadExceptionsXlsx } from "../../utils/exceptions-xlsx-export";
import { buildExceptionsExportFileName } from "../../utils/exceptions-export-filename";
import {
  INVENTORY_KPI,
  activeInventoryKpi,
  exceptionFilterOptions,
  filterExceptionOrders,
  nextInventoryStatusesForKpiClick,
} from "../../utils/exceptions-report-filters";
import HeaderFilter from "./header-filter";

const font = { fontFamily: "Poppins" };

const badgeSx = (status = "") => {
  if (
    status === "Inventory Available" ||
    status === "Available Now" ||
    status.startsWith("Available at ")
  ) {
    return {
      color: "#276749",
      borderColor: "#276749",
      backgroundColor: "transparent",
    };
  }
  if (
    status.includes("Short") ||
    status.includes("Split") ||
    status.includes("Incoming") ||
    status.includes("Partial")
  ) {
    return {
      color: "#975a16",
      borderColor: "#d69e2e",
      backgroundColor: "transparent",
    };
  }
  if (status.includes("Not Available") || status === "No Stock") {
    return {
      color: "#9b2c2c",
      borderColor: "#c53030",
      backgroundColor: "transparent",
    };
  }
  return {
    color: "#4a5568",
    borderColor: "#a0aec0",
    backgroundColor: "transparent",
  };
};

const StatusChip = ({ label }) => (
  <Chip
    size="small"
    variant="outlined"
    label={label}
    sx={{ ...font, ...badgeSx(label), fontWeight: 600 }}
  />
);

const kpiBorderColor = (tone) =>
  tone === "available"
    ? "#276749"
    : tone === "partial"
      ? "#d69e2e"
      : tone === "unavailable"
        ? "#c53030"
        : "divider";

const kpiSelectedBackground = (tone) =>
  tone === "available"
    ? "rgba(39, 103, 73, 0.08)"
    : tone === "partial"
      ? "rgba(214, 158, 46, 0.10)"
      : tone === "unavailable"
        ? "rgba(197, 48, 48, 0.08)"
        : "action.selected";

const Kpi = ({ label, value, tone, selected, onClick, ariaLabel }) => (
  <Paper
    component={ButtonBase}
    elevation={0}
    focusRipple={false}
    disableRipple
    onClick={onClick}
    aria-pressed={selected}
    aria-label={ariaLabel}
    title={`Click to ${label === "Total Exception Orders" ? "show all exception orders" : `filter by ${label}`}`}
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 140,
      display: "block",
      textAlign: "left",
      cursor: "pointer",
      userSelect: "none",
      border: "1px solid",
      borderColor: kpiBorderColor(tone),
      borderRadius: 2,
      backgroundColor: selected ? kpiSelectedBackground(tone) : "transparent",
      boxShadow: selected ? 1 : "none",
      transition: "background-color 0.12s ease, box-shadow 0.12s ease",
      "&:hover": {
        boxShadow: 1,
        backgroundColor: selected ? kpiSelectedBackground(tone) : "action.hover",
      },
      "&:focus-visible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: 2,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        color: selected ? "text.primary" : "text.secondary",
      }}
    >
      <FilterListIcon sx={{ fontSize: 14, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{ ...font, color: "inherit", lineHeight: 1.3 }}
      >
        {label}
      </Typography>
    </Box>
    <Typography sx={{ ...font, fontWeight: 700, fontSize: 22 }}>
      {value}
    </Typography>
  </Paper>
);

const MAX_VISIBLE_TAGS = 2;

const OrderTagsCell = ({ tags }) => {
  const values = Array.isArray(tags) ? tags.filter(Boolean) : [];
  if (!values.length) return "";
  const visibleTags = values.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagCount = values.length - visibleTags.length;
  const tagsTooltip = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {values.map((tag) => (
        <span key={tag} style={{ fontSize: 11, fontFamily: "Poppins" }}>
          {tag}
        </span>
      ))}
    </Box>
  );
  return (
    <Tooltip title={tagsTooltip} arrow placement="top">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: 0.5,
          maxWidth: 180,
          overflow: "hidden",
          pr: 0.5,
        }}
      >
        {visibleTags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              maxWidth: 80,
              minWidth: 0,
              flexShrink: 1,
              borderColor: "var(--border-color)",
              "& .MuiChip-label": {
                fontSize: 11,
                fontFamily: "Poppins",
                color: "var(--text-secondary)",
                paddingInline: "6px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        ))}
        {hiddenTagCount > 0 && (
          <Typography
            fontSize="11px"
            color="text.secondary"
            fontWeight={500}
            sx={{ ...font, whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {`+${hiddenTagCount}`}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const warehouseFulfillmentLabel = (warehouse) => {
  if (warehouse?.can_fulfill_order) {
    return "Can fulfill whole order";
  }
  if ((warehouse?.skus || []).some((sku) => sku.data_unavailable)) {
    return "Incomplete inventory data";
  }
  return "Cannot fulfill whole order";
};

const WarehouseInventorySection = ({ warehouses }) => {
  if (!warehouses?.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ ...font, fontWeight: 600, mb: 1 }}>
        Warehouse inventory
      </Typography>
      {warehouses.map((warehouse) => (
        <Box
          key={warehouse.warehouse_id}
          sx={{
            mb: 1.5,
            border: "1px solid",
            borderColor: warehouse.can_fulfill_order ? "#276749" : "divider",
            borderRadius: 1,
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              px: 1.25,
              py: 0.75,
            }}
          >
            <Typography sx={{ ...font, fontWeight: 600, fontSize: 13 }}>
              {warehouse.warehouse_name}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={warehouseFulfillmentLabel(warehouse)}
              sx={{
                ...font,
                fontWeight: 600,
                height: 22,
                ...(warehouse.can_fulfill_order
                  ? {
                      color: "#276749",
                      borderColor: "#276749",
                    }
                  : {}),
              }}
            />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={font}>SKU</TableCell>
                <TableCell sx={{ ...font, fontWeight: 600 }} align="right">
                  Ordered
                </TableCell>
                <TableCell sx={{ ...font, fontWeight: 600 }} align="right">
                  On hand
                </TableCell>
                <TableCell sx={{ ...font, fontWeight: 600 }} align="right">
                  In transit
                </TableCell>
                <TableCell sx={font}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(warehouse.skus || []).map((sku) => (
                <TableRow key={sku.sku}>
                  <TableCell sx={font}>{sku.sku}</TableCell>
                  <TableCell sx={font} align="right">
                    {sku.ordered_quantity}
                  </TableCell>
                  <TableCell sx={font} align="right">
                    {sku.in_hand ?? "—"}
                  </TableCell>
                  <TableCell sx={font} align="right">
                    {sku.in_transit ?? "—"}
                  </TableCell>
                  <TableCell>
                    <StatusChip label={sku.sku_status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
};

const inventoryColumnLabel = (label, networkTotals) => (
  <>
    {label}
    {networkTotals && (
      <Typography
        variant="caption"
        display="block"
        color="text.secondary"
        sx={font}
      >
        Across warehouses
      </Typography>
    )}
  </>
);

const ExpandedSkus = ({ order }) => {
  const networkTotals = order.warehouse_assigned === false;
  return (
  <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
    {order.inventory_analysis_informational && (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        Inventory numbers are informational because this order status is
        normally closed.
      </Alert>
    )}
    <Typography sx={{ ...font, mb: 1 }}>{order.insight}</Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={font}>SKU</TableCell>
          <TableCell sx={font}>Item/Product</TableCell>
          <TableCell sx={font} align="right">
            Ordered
          </TableCell>
          <TableCell sx={font} align="right">
            {inventoryColumnLabel("Total Inventory", networkTotals)}
          </TableCell>
          <TableCell sx={font} align="right">
            {inventoryColumnLabel("In Hand", networkTotals)}
          </TableCell>
          <TableCell sx={font} align="right">
            {inventoryColumnLabel("In Transit", networkTotals)}
          </TableCell>
          <TableCell sx={font} align="right">
            Immediate Shortfall
          </TableCell>
          <TableCell sx={font} align="right">
            Remaining Shortfall
          </TableCell>
          <TableCell sx={font}>SKU Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(order.skus || []).map((sku) => (
          <TableRow key={sku.sku}>
            <TableCell sx={font}>{sku.sku}</TableCell>
            <TableCell sx={font}>{sku.name}</TableCell>
            <TableCell sx={font} align="right">
              {sku.ordered_quantity}
            </TableCell>
            <TableCell sx={font} align="right">
              {sku.quantity ?? "—"}
            </TableCell>
            <TableCell sx={font} align="right">
              {sku.in_hand ?? "—"}
            </TableCell>
            <TableCell sx={font} align="right">
              {sku.in_transit ?? "—"}
            </TableCell>
            <TableCell sx={font} align="right">
              {sku.immediate_shortfall ?? "—"}
            </TableCell>
            <TableCell sx={font} align="right">
              {sku.remaining_shortfall ?? "—"}
            </TableCell>
            <TableCell>
              <StatusChip label={sku.sku_status} />
              {sku.explanation && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {sku.explanation}
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <WarehouseInventorySection warehouses={order.warehouse_breakdown} />
  </Box>
  );
};

const OrderRow = ({ order }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((value) => !value)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ ...font, fontWeight: 600 }}>{order.order_id}</TableCell>
        <TableCell sx={font}>{order.order_date_display}</TableCell>
        <TableCell sx={font}>{order.status}</TableCell>
        <TableCell sx={font}>{order.source}</TableCell>
        <TableCell sx={font}>
          <OrderTagsCell tags={order.tags} />
        </TableCell>
        <TableCell sx={font}>{order.warehouse}</TableCell>
        <TableCell sx={font} align="right">
          {order.sku_count}
        </TableCell>
        <TableCell sx={font} align="right">
          {order.units}
        </TableCell>
        <TableCell>
          <StatusChip label={order.inventory_status} />
        </TableCell>
        <TableCell sx={font} align="right">
          {order.age_days}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <ExpandedSkus order={order} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const emptyFilters = {
  orderId: "",
  statuses: [],
  sources: [],
  warehouses: [],
  inventoryStatuses: [],
};

const ExceptionsReport = ({
  report,
  onClose,
  layout = "dialog",
  exportActionsRef,
  filtersResetKey = 0,
}) => {
  const [columnFilters, setColumnFilters] = useState(emptyFilters);
  const filterEpoch = `${report?.generated_at ?? ""}:${filtersResetKey}`;
  const [appliedFilterEpoch, setAppliedFilterEpoch] = useState(filterEpoch);
  const filtersNeedReset = filterEpoch !== appliedFilterEpoch;
  if (filtersNeedReset) {
    setAppliedFilterEpoch(filterEpoch);
    setColumnFilters(emptyFilters);
  }
  const appliedFilters = filtersNeedReset ? emptyFilters : columnFilters;
  const orders = report?.orders || [];
  const options = exceptionFilterOptions(orders);
  const visibleOrders = filterExceptionOrders(orders, appliedFilters);
  const isPage = layout === "page";
  const selectedKpi = activeInventoryKpi(
    appliedFilters.inventoryStatuses,
    options.inventoryStatuses,
  );

  const applyInventoryKpi = (kpi) => {
    setColumnFilters((current) => ({
      ...current,
      inventoryStatuses: nextInventoryStatusesForKpiClick(
        kpi,
        current.inventoryStatuses,
        options.inventoryStatuses,
      ),
    }));
  };

  useEffect(() => {
    if (!exportActionsRef) return undefined;
    if (!report) {
      exportActionsRef.current = null;
      return undefined;
    }
    exportActionsRef.current = {
      downloadHtml: () =>
        downloadExceptionsHtml(
          { ...report, orders: visibleOrders },
          buildExceptionsExportFileName(report, "html"),
        ),
      downloadExcel: () =>
        downloadExceptionsXlsx(
          { ...report, orders: visibleOrders },
          buildExceptionsExportFileName(report, "xlsx"),
        ),
    };
    return () => {
      exportActionsRef.current = null;
    };
  }, [exportActionsRef, report, visibleOrders]);

  if (!report) return null;
  const kpis = report.kpis || {};
  const filters = report.filters || {};

  const body = (
    <>
      {!isPage && (
        <Typography variant="body2" sx={{ ...font, color: "text.secondary", mb: 1 }}>
          These orders are not fulfilled. Do we have the inventory to fulfill
          them?
        </Typography>
      )}
      <Typography variant="body2" sx={{ ...font, color: "text.secondary", mb: 2 }}>
        Timezone: {filters.timezone}. {filters.start_display} →{" "}
        {filters.end_display}. Statuses: {(filters.statuses || []).join(", ")}.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Inventory Available means on-hand inventory can satisfy the order
        now. The order is still an exception because it is unfulfilled.
      </Alert>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Kpi
          label="Total Exception Orders"
          value={kpis.total || 0}
          selected={selectedKpi === INVENTORY_KPI.TOTAL}
          onClick={() => applyInventoryKpi(INVENTORY_KPI.TOTAL)}
          ariaLabel="Show all exception orders"
        />
        <Kpi
          label="Inventory Available"
          value={kpis.inventory_available || 0}
          tone="available"
          selected={selectedKpi === INVENTORY_KPI.AVAILABLE}
          onClick={() => applyInventoryKpi(INVENTORY_KPI.AVAILABLE)}
          ariaLabel="Filter to inventory available at any warehouse"
        />
        <Kpi
          label="Inventory Short"
          value={kpis.inventory_short || 0}
          tone="partial"
          selected={selectedKpi === INVENTORY_KPI.SHORT}
          onClick={() => applyInventoryKpi(INVENTORY_KPI.SHORT)}
          ariaLabel="Filter to inventory short"
        />
        <Kpi
          label="Inventory Not Available"
          value={kpis.inventory_not_available || 0}
          tone="unavailable"
          selected={selectedKpi === INVENTORY_KPI.NOT_AVAILABLE}
          onClick={() => applyInventoryKpi(INVENTORY_KPI.NOT_AVAILABLE)}
          ariaLabel="Filter to inventory not available"
        />
        <Kpi
          label="Inventory Data Not Available"
          value={kpis.inventory_data_not_available || 0}
          selected={selectedKpi === INVENTORY_KPI.DATA_NOT_AVAILABLE}
          onClick={() => applyInventoryKpi(INVENTORY_KPI.DATA_NOT_AVAILABLE)}
          ariaLabel="Filter to inventory data not available"
        />
      </Box>
      {report.empty ? (
        <Alert severity="success">
          No orders matched the selected statuses and datetime range.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={font}>
                <HeaderFilter
                  label="Order ID"
                  type="search"
                  value={appliedFilters.orderId}
                  active={Boolean(appliedFilters.orderId)}
                  onApply={(orderId) =>
                    setColumnFilters((current) => ({ ...current, orderId }))
                  }
                />
              </TableCell>
              <TableCell sx={font}>Order Date/Time</TableCell>
              <TableCell sx={font}>
                <HeaderFilter
                  label="Status"
                  options={options.statuses}
                  value={appliedFilters.statuses}
                  active={appliedFilters.statuses.length > 0}
                  onApply={(statuses) =>
                    setColumnFilters((current) => ({ ...current, statuses }))
                  }
                />
              </TableCell>
              <TableCell sx={font}>
                <HeaderFilter
                  label="Source"
                  options={options.sources}
                  value={appliedFilters.sources}
                  active={appliedFilters.sources.length > 0}
                  onApply={(sources) =>
                    setColumnFilters((current) => ({ ...current, sources }))
                  }
                />
              </TableCell>
              <TableCell sx={{ ...font, fontWeight: 600 }}>Tags</TableCell>
              <TableCell sx={font}>
                <HeaderFilter
                  label="Warehouse"
                  options={options.warehouses}
                  value={appliedFilters.warehouses}
                  active={appliedFilters.warehouses.length > 0}
                  onApply={(warehouses) =>
                    setColumnFilters((current) => ({
                      ...current,
                      warehouses,
                    }))
                  }
                />
              </TableCell>
              <TableCell sx={{ ...font, fontWeight: 600 }} align="right">
                SKUs
              </TableCell>
              <TableCell sx={{ ...font, fontWeight: 600 }} align="right">
                Units
              </TableCell>
              <TableCell sx={font}>
                <HeaderFilter
                  label="Inventory Status"
                  options={options.inventoryStatuses}
                  value={appliedFilters.inventoryStatuses}
                  active={appliedFilters.inventoryStatuses.length > 0}
                  onApply={(inventoryStatuses) =>
                    setColumnFilters((current) => ({
                      ...current,
                      inventoryStatuses,
                    }))
                  }
                />
              </TableCell>
              <TableCell sx={{ ...font, fontWeight: 600 }} align="right">
                Age (days)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleOrders.map((order) => (
              <OrderRow key={order.order_id} order={order} />
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );

  if (isPage) {
    return <Box sx={{ overflowX: "auto" }}>{body}</Box>;
  }

  return (
    <>
      <DialogTitle sx={{ ...font, fontWeight: 600 }}>
        Exceptions / Unfulfilled Orders
      </DialogTitle>
      <DialogContent sx={{ minHeight: 0, overflowX: "auto" }}>
        {body}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() =>
            downloadExceptionsHtml(
              { ...report, orders: visibleOrders },
              buildExceptionsExportFileName(report, "html"),
            )
          }
          disabled={report.empty}
        >
          Download HTML
        </Button>
        <Button
          onClick={() =>
            downloadExceptionsXlsx(
              { ...report, orders: visibleOrders },
              buildExceptionsExportFileName(report, "xlsx"),
            )
          }
          disabled={report.empty}
        >
          Download Excel
        </Button>
        {onClose && (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default ExceptionsReport;
